import React, { useState } from "react"

import { ChevronDown, ChevronRight } from "lucide-react"

import { TableCell, TableRow } from "@/components/ui/table"

interface ListViewShiftSectionProps {
  title: string
  count: number
  /** Right-hand side of the header, e.g. the date on the schedule list. */
  meta?: React.ReactNode
  defaultExpanded?: boolean
  children: React.ReactNode
}

export function ListViewShiftSection({
  title,
  count,
  meta,
  defaultExpanded = true,
  children,
}: ListViewShiftSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded)

  return (
    <div className="border rounded-lg overflow-hidden">
      <div
        className="bg-palette-jungleGreen000 px-4 py-3 border-b cursor-pointer hover:bg-palette-jungleGreen100 transition-colors"
        onClick={() => setExpanded((previous) => !previous)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronDown className="h-5 w-5 text-palette-jungleGreen600" />
            ) : (
              <ChevronRight className="h-5 w-5 text-palette-jungleGreen600" />
            )}
            <h3 className="font-semibold text-palette-jungleGreen900">
              {title} ({count})
            </h3>
          </div>
          {meta && <span className="text-sm text-palette-jungleGreen600">{meta}</span>}
        </div>
      </div>
      {expanded && <div className="overflow-x-auto">{children}</div>}
    </div>
  )
}

interface ShiftSubGroupRowProps {
  label: string
  count: number
  expanded: boolean
  onToggle: () => void
  colSpan: number
}

// A sub-heading inside a section's table, sitting in the table's own grid rather than above it.
export function ShiftSubGroupRow({
  label,
  count,
  expanded,
  onToggle,
  colSpan,
}: ShiftSubGroupRowProps) {
  return (
    <TableRow
      className="bg-palette-jungleGreen000/60 cursor-pointer hover:bg-palette-jungleGreen100"
      onClick={onToggle}
    >
      <TableCell colSpan={colSpan}>
        <div className="flex items-center justify-between text-sm text-palette-jungleGreen800">
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-palette-jungleGreen600" />
            ) : (
              <ChevronRight className="h-4 w-4 text-palette-jungleGreen600" />
            )}
            <span className="font-medium">{label}</span>
          </div>
          <span className="text-xs text-palette-jungleGreen500">
            {count} shift{count === 1 ? "" : "s"}
          </span>
        </div>
      </TableCell>
    </TableRow>
  )
}
